import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable, throwError, timer } from 'rxjs'
import { retry } from 'rxjs/operators'

/** Maximum number of retry attempts before giving up. Matches AR's own Python SDK default (retry_strategy_total = 25). */
const MAX_RETRIES = 25

/** Base delay in milliseconds — doubles on each retry before jitter is applied. */
const BASE_DELAY_MS = 100

const HTTP_STATUS_RETRY = 503
const HTTP_STATUS_GATEWAYERROR = 504
/** Status 0 is how Chrome surfaces a network-level failure (e.g. no connection). */
const CHROME_STATUS_ERROR = 0

const retryableErrors = [HTTP_STATUS_RETRY, HTTP_STATUS_GATEWAYERROR, CHROME_STATUS_ERROR]

const isRetryable = (error: HttpEvent<unknown>): boolean =>
  error instanceof HttpErrorResponse && retryableErrors.includes(error.status)

const fatal = (error: HttpEvent<unknown>): string => {
  if (error instanceof HttpErrorResponse) {
    return `Status ${error.status}: ${error.statusText} calling ${error.url ?? ''}`
  }
  return 'An unknown error occurred during the http operation'
}

/** Returns the Retry-After delay in ms if the header is present, otherwise null. */
const retryAfterMs = (error: HttpEvent<unknown>): number | null => {
  if (!(error instanceof HttpErrorResponse)) return null
  const header = error.headers.get('Retry-After')
  if (!header) return null
  const deltaSeconds = Number(header)
  if (!isNaN(deltaSeconds)) return deltaSeconds * 1000
  const date = Date.parse(header)
  if (!isNaN(date)) return Math.max(0, date - Date.now())
  return null
}

/**
 * Global HTTP interceptor that automatically retries requests on transient server errors
 * (503 Service Unavailable, 504 Gateway Timeout, Chrome network failures).
 *
 * **Retry-After header:** AnalyzeRe includes a Retry-After header on 503 responses for
 * long-running operations. When present, that value is used directly as the wait delay.
 * Exponential backoff is the fallback for responses that omit it (e.g. 504, network errors).
 * See https://docs.analyzere.net/#errors
 *
 * **Backoff strategy (fallback):** Exponential with ±50% jitter.
 * Each retry waits `BASE_DELAY_MS * 2^retryCount` plus a random fraction of that value.
 * Jitter prevents synchronized retry storms when many concurrent requests (e.g. from a forkJoin)
 * all fail at the same moment — without it, every stream retries in lockstep and hits the
 * server with another equally-sized burst.
 *
 * **Concurrency:** This interceptor retries each request independently. It does not cap the
 * number of in-flight requests. If AnalyzeRe provides a recommended concurrency limit, add a
 * per-service semaphore upstream of the HTTP call rather than here.
 *
 * @see {@link https://learn.microsoft.com/en-us/azure/architecture/patterns/retry General details on the retry pattern}
 */
@Injectable()
export class RetryInterceptor implements HttpInterceptor {
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      retry({
        delay: (error: HttpEvent<unknown>, retryCount: number) => {
          if (!isRetryable(error)) {
            return throwError(() => fatal(error))
          }
          if (retryCount > MAX_RETRIES) {
            return throwError(
              () => `Unable to complete http operation after ${retryCount - 1} attempts.`,
            )
          }
          const serverDelay = retryAfterMs(error)
          if (serverDelay !== null) return timer(serverDelay)
          const baseDelay = BASE_DELAY_MS * Math.pow(2, retryCount)
          const jitter = (Math.random() - 0.5) * baseDelay
          return timer(baseDelay + jitter)
        },
      }),
    )
  }
}
